
import React from 'react';
import { Shield } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

interface AuthenticationTabProps {
  editing: boolean;
}

export const AuthenticationTab: React.FC<AuthenticationTabProps> = ({ editing }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between space-x-2">
        <div className="space-y-0.5">
          <div className="flex items-center">
            <Shield className="h-5 w-5 mr-2 text-primary" />
            <h3 className="font-medium text-lg">Authentication Settings</h3>
          </div>
          <p className="text-muted-foreground text-sm">
            Configure how users authenticate with the system
          </p>
        </div>
      </div>
      
      <Separator />
      
      <div className="grid gap-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base">Multi-Factor Authentication</Label>
            <p className="text-sm text-muted-foreground">Require MFA for all users</p>
          </div>
          <Switch id="mfa" disabled={!editing} />
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base">Password Complexity</Label>
            <p className="text-sm text-muted-foreground">Enforce stronger password requirements</p>
          </div>
          <Switch id="password-complexity" checked disabled={!editing} />
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base">Single Sign-On (SSO)</Label>
            <p className="text-sm text-muted-foreground">Enable SSO integration</p>
          </div>
          <Switch id="sso" disabled={!editing} />
        </div>
      </div>
      
      <div className="grid gap-4 pt-4">
        <div className="grid gap-2">
          <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
          <Input 
            id="session-timeout" 
            type="number" 
            defaultValue="30" 
            min="1"
            max="1440"
            disabled={!editing}
          />
        </div>
        
        <div className="grid gap-2">
          <Label htmlFor="auth-method">Default Authentication Method</Label>
          <Select defaultValue="jwt" disabled={!editing}>
            <SelectTrigger id="auth-method">
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="jwt">JWT Token</SelectItem>
              <SelectItem value="oauth">OAuth 2.0</SelectItem>
              <SelectItem value="saml">SAML</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
